// The one moment this app celebrates, as physics. Lifted as is from the Andon
// app's `lib/confetti.ts`.
//
// WHY A HUNDRED LINES INSTEAD OF A PACKAGE
//
// Every confetti library on npm is a canvas, a particle array and a gravity
// constant. This app's CSP allows no third-party origin and its dependency list
// is short on purpose, so the trade is: carry a small amount of arithmetic, or
// carry a dependency, its transitive tree and a bundle it draws with settings
// we would immediately override. The arithmetic is here.
//
// AND IT IS SEPARATE FROM THE CANVAS, for the same reason every other `lib/`
// module here is: the part worth testing is what the particles DO — that they
// fan out rather than falling in a line, that they die — and the part not
// worth testing is `ctx.fillRect`. Keeping them apart means these rules can be
// asserted without a DOM, which this client's test setup deliberately does not
// have.

/** One piece of paper in the air. Positions are in pixels, times in ms. */
export interface Particle {
  x: number;
  y: number;
  /** Pixels per second. */
  vx: number;
  vy: number;
  /** Radians, and the rate it is turning at. A flat rectangle needs both. */
  rot: number;
  spin: number;
  /** Half-width and half-height, so the draw is symmetrical about (x, y). */
  w: number;
  h: number;
  color: string;
  /** Milliseconds left. Fades over the last third of it. */
  life: number;
  ttl: number;
}

/**
 * The palette.
 *
 * The app's own colours rather than a rainbow: this is the andon's green
 * saying the thing is fixed, in the same family as every other "good" in the
 * interface. The two neutrals stop it reading as a solid green cloud, and the
 * amber is there because a burst of one hue at speed looks like a glitch.
 *
 * Fixed hex rather than the CSS variables, deliberately. This draws on a canvas
 * where `var(--pos)` does not resolve, and both themes want the same paper.
 */
export const CONFETTI_COLORS = ['#37c77e', '#6ee7a8', '#f0a13a', '#f3f5f7', '#8a909a'] as const;

export interface BurstOptions {
  /** Where it comes from, in canvas pixels. */
  x: number;
  y: number;
  count: number;
  /** How hard, in pixels per second. A full-screen burst has to cross a phone. */
  power: number;
  /** How long each piece lives, in ms. */
  ttl: number;
  /** Injected so a test can assert the spread rather than hope for it. */
  random?: () => number;
}

/** How wide a piece of paper is, at the small end and the large. */
const MIN_SIZE = 3;
const SIZE_RANGE = 5;

/**
 * A burst, thrown UPWARD and outward.
 *
 * The angles run from −165° to −15° — a fan across the top rather than a full
 * circle — because a full circle sends half the paper straight down through
 * whatever it came out of and reads as a leak rather than a celebration.
 * Gravity brings it back through the middle a moment later, which is the shape
 * everybody recognises.
 */
export function makeParticles(opts: BurstOptions): Particle[] {
  const random = opts.random ?? Math.random;
  const out: Particle[] = [];
  for (let i = 0; i < opts.count; i++) {
    const angle = -Math.PI + Math.PI / 12 + (random() * (Math.PI * 10)) / 12;
    // Square-rooted, so the speeds spread evenly through the fan instead of
    // clumping at the fast end and arriving as one wall.
    const speed = opts.power * (0.35 + 0.65 * Math.sqrt(random()));
    out.push({
      x: opts.x,
      y: opts.y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      rot: random() * Math.PI * 2,
      spin: (random() - 0.5) * 12,
      w: MIN_SIZE + random() * SIZE_RANGE,
      h: MIN_SIZE + random() * SIZE_RANGE,
      color: CONFETTI_COLORS[Math.floor(random() * CONFETTI_COLORS.length)]!,
      // Staggered, so the last pieces do not all vanish on the same frame.
      life: opts.ttl * (0.7 + random() * 0.3),
      ttl: opts.ttl,
    });
  }
  return out;
}

/** Pixels per second per second. Heavier than real paper; it has a screen to clear. */
const GRAVITY = 900;
/** Air, per second. Kills the sideways speed faster than the falling speed. */
const DRAG = 1.9;

/**
 * Advance every piece, drop the dead ones, and say whether any are left.
 *
 * `dt` is CLAMPED. A tab that was backgrounded mid-burst comes back with a
 * frame gap of several seconds, and integrating that in one step throws every
 * particle several screens away — so a long gap is treated as one slow frame,
 * which simply ends the burst a little early.
 */
export function stepParticles(list: Particle[], dtMs: number): Particle[] {
  const dt = Math.min(dtMs, 64) / 1000;
  const alive: Particle[] = [];
  for (const p of list) {
    p.life -= dtMs;
    if (p.life <= 0) continue;
    p.vy += GRAVITY * dt;
    p.vx -= p.vx * DRAG * dt;
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    p.rot += p.spin * dt;
    alive.push(p);
  }
  return alive;
}

/** 0…1, fading over the last third of a piece's life. */
export function opacityOf(p: Particle): number {
  const fadeFrom = p.ttl / 3;
  return p.life >= fadeFrom ? 1 : Math.max(0, p.life / fadeFrom);
}
